import { outstandingQuantity, poUsdRate } from "./po";

const cents = (n: number) => Math.round(n * 100) / 100;

export type BudgetSpendPo = {
  orderStatus: string;
  /** Sertica's stored date; cut on the calendar date as stored, no time-zone conversion. */
  orderDate: string | null;
  /** USD per 100 units of the order currency (rounded); see poUsdRate. */
  exchangeRate: number;
  /** Order-currency and USD totals: with both, the rate is the one Sertica applied. */
  totalCost?: number | null;
  totalCostsUsd?: number | null;
};

export type BudgetSpendPoLine = {
  orderedQuantity: number;
  receivedQuantity: number;
  unitPrice: number;
  discountPercentage: number;
  /** In order currency, line discount already applied. */
  totalPrice: number;
  /** Rejected on receipt: no longer committed (outstandingQuantity). */
  rejectedQuantity?: number;
};

export type Quarter = 1 | 2 | 3 | 4;

export type BudgetSpend = {
  year: number;
  quarter: Quarter;
  committedUsd: number;
  accruedUsd: number;
  /** Ordered or Partly received: the PO can still carry the flag. */
  open: boolean;
};

/**
 * What one PO line puts against a budget, and the period it counts in (SPEC §4.3–4.4): the year
 * and quarter of the PO's order date, committed worked out as PO pending receipt, accrued as the
 * received part at the PO rate. Unrounded, so a PO's lines add up to its USD total exactly, as
 * Sertica adds them; figures are rounded only when shown, and over budget is judged in cents.
 */
export function poLineBudgetSpend(po: BudgetSpendPo, line: BudgetSpendPoLine): BudgetSpend | null {
  if (po.orderStatus === "NotOrdered" || po.orderStatus === "Cancelled" || !po.orderDate)
    return null;
  const year = Number(po.orderDate.slice(0, 4));
  const month = Number(po.orderDate!.slice(5, 7));
  const rate = poUsdRate(po);
  const netUnitUsd = line.unitPrice * (1 - line.discountPercentage / 100) * rate;
  const openUsd = po.orderStatus === "Ordered" && !line.rejectedQuantity ? line.totalPrice * rate : outstandingQuantity(line) * netUnitUsd;
  return {
    year,
    quarter: Math.ceil(month / 3) as Quarter,
    committedUsd: openUsd,
    accruedUsd: line.receivedQuantity * netUnitUsd,
    open: po.orderStatus === "Ordered" || po.orderStatus === "PartlyReceived",
  };
}

/** The current budget period's quarter: the year and quarter of today's Sertica date (SPEC §4.3). */
export function currentQuarter(today: string): { year: number; quarter: Quarter } {
  return { year: Number(today.slice(0, 4)), quarter: Math.ceil(Number(today.slice(5, 7)) / 3) as Quarter };
}

/** A period in the URL and the API: "2026-Q3" or "2026". Anything else is the current quarter. */
export function parseBudgetPeriod(s: string | undefined, today: string): FollowUpPeriod {
  const m = /^(\d{4})(?:-Q([1-4]))?$/.exec(s ?? "");
  return m ? { year: Number(m[1]), quarter: m[2] ? (Number(m[2]) as Quarter) : null } : currentQuarter(today);
}

export const formatBudgetPeriod = (p: FollowUpPeriod) => (p.quarter === null ? String(p.year) : `${p.year}-Q${p.quarter}`);

/** An overrun below this many cents is rounding (exchange rates, sub-cent postings), not over budget. */
export const OVER_BUDGET_TOLERANCE_CENTS = 100;

/** Whether `usd` exceeds `limitUsd` by $1.00 or more, both rounded to cents. */
const exceeds = (usd: number, limitUsd: number) => Math.round(usd * 100) - Math.round(limitUsd * 100) >= OVER_BUDGET_TOLERANCE_CENTS;

/**
 * Over budget (SPEC §3.2): committed + accrued above the budget by $1.00 or more, both rounded to
 * cents. A line less than $1 over is not over (its remaining still shows the cents); a zero budget
 * is over with $1 of spend; a line that is not comparable (no budget figure) is never over.
 */
export function isOverBudget(budgetUsd: number | null, f: { committedUsd: number; accruedUsd: number }): boolean {
  return budgetUsd !== null && exceeds(f.committedUsd + f.accruedUsd, budgetUsd);
}

export type BudgetLineInput = {
  purchaseTypeNo: string;
  /** Null on a parent line (a purchase-type subtotal). */
  technicalAccountNo: string | null;
  /** `monthAmount1..12`, a missing key as 0. */
  monthAmounts: number[];
  /** The line's currency, "mixed", or null when it carries no amount (then it is comparable). */
  currencyNo: string | null;
};

export type BudgetInput = {
  budgetNo: string;
  unitNo: string;
  status: string;
  /** The period's number, e.g. "Year 2026" (its name reads "Budget Period 2026"). */
  budgetPeriodNo: string | null;
  updateDate: string;
  monetaryUnit: number;
  lines: BudgetLineInput[];
  /** Shown on the sheet heading (SPEC §8.6); not used by the join. */
  unitName?: string | null;
  companyName?: string | null;
  /** The forecast whose months the lines carry instead of the budget's (SPEC §4.3 item 5); null when none is used. */
  forecast?: BudgetForecast | null;
};

/** A budget's current forecast in Sertica (research/forecasts.md). */
export type BudgetForecast = { forecastNo: string; forecastMonth: string | null };

/** A requisition line behind a PO line (`requisition_po_position_links`), with its requisition's coding. */
export type SpendLink = {
  /** Carried for the line drawer, the export and the Over budget flag; the join does not use them. */
  requisitionNo?: string;
  reqPositionNo?: number;
  unitNo: string;
  purchaseTypeNo: string | null;
  technicalAccountNo: string | null;
  orderedQuantity: number;
  projectNo: string | null;
  projectName: string | null;
};

/** One PO line's budget spend (see `poLineBudgetSpend`) and the requisition lines it came from. */
export type SpendLine = BudgetSpend & {
  /** Carried for the line drawer and the export; the join does not use them. */
  poNo?: string;
  positionNo?: number;
  /** The PO's unit, used only when the line has no requisition link (uncoded). */
  poUnitNo: string | null;
  links: SpendLink[];
};

export type FollowUpPeriod = { year: number; quarter: Quarter | null };

export type FollowUpInput = {
  budgets: BudgetInput[];
  spend: SpendLine[];
  /** Admin setting (SPEC §13.1). */
  projectFunded: string[];
  period: FollowUpPeriod;
};

type Figures = {
  committedUsd: number;
  accruedUsd: number;
  /** POs under approval (not ordered yet) on the row; only in a period holding the current quarter. */
  otherCostUsd?: number;
};

export type UnbudgetedReason =
  | "no_live_budget"
  | "purchase_type_not_budgeted"
  | "technical_account_not_budgeted"
  | "uncoded";

export type BudgetRow =
  | (Figures & {
      kind: "parent";
      purchaseTypeNo: string;
      /** Null when any child is not comparable. */
      budgetUsd: number | null;
      remainingUsd: number | null;
      /** Child lines over budget in the period shown. */
      overCount: number;
    })
  | (Figures & {
      kind: "line";
      purchaseTypeNo: string;
      technicalAccountNo: string;
      /** Null when not comparable (§4.4): a non-USD amount, or a budget with monetaryUnit ≠ 1. */
      budgetUsd: number | null;
      remainingUsd: number | null;
      /** Over budget in the period shown (SPEC §3.2). */
      over: boolean;
      /** An open PO (Ordered or Partly received) puts spend on it in the period shown. */
      openPo: boolean;
    })
  | (Figures & {
      kind: "unbudgeted";
      reason: UnbudgetedReason;
      purchaseTypeNo: string | null;
      technicalAccountNo: string | null;
    });

/** How a budget's money is entered, for the sheet's note (SPEC §4.3 item 4). */
export type QuarterRule = "january" | "per_quarter";

export type UnitSheet = {
  unitNo: string | null;
  unitName: string | null;
  budgetNo: string | null;
  status: string | null;
  companyName: string | null;
  quarterRule: QuarterRule | null;
  forecast: BudgetForecast | null;
  /** More than one budget qualified; the latest updated is used (§4.2 item 2). */
  ambiguous: boolean;
  rows: BudgetRow[];
  /** Child lines, and those over budget in the period shown. */
  lineCount: number;
  overCount: number;
};

/**
 * One row of the project list (SPEC §4.2 item 5): spend coded to a project, whatever its purchase
 * type, by project → purchase type → technical account, split by where the join placed it. Project
 * spend with no project is here too, under a null project.
 */
export type ProjectRow = Figures & {
  projectNo: string | null;
  projectName: string | null;
  purchaseTypeNo: string | null;
  technicalAccountNo: string | null;
  /** Project spend (a project-funded purchase type no unit budget carries); else on a unit budget. */
  projectSpend: boolean;
  /** The units that ordered under it. */
  unitNos: string[];
};

/**
 * A project, or one of its rows, as the project drawer opens it (SPEC §8.6): the project alone
 * takes all its rows; a purchase type, technical account and where it was charged narrow it to one.
 */
export type ProjectRef = {
  projectNo: string | null;
  purchaseTypeNo?: string | null;
  technicalAccountNo?: string | null;
  projectSpend?: boolean;
};

/** Whether a coding, charged as project spend or not, falls under a project ref, as the Projects list groups it. */
export function onProject(ref: ProjectRef, k: { projectNo: string | null; purchaseTypeNo: string | null; technicalAccountNo: string | null }, projectSpend: boolean) {
  return (
    k.projectNo === ref.projectNo &&
    // Spend with no project is on the list only as project spend.
    (k.projectNo !== null || projectSpend) &&
    (ref.purchaseTypeNo === undefined || k.purchaseTypeNo === ref.purchaseTypeNo) &&
    (ref.technicalAccountNo === undefined || k.technicalAccountNo === ref.technicalAccountNo) &&
    (ref.projectSpend === undefined || projectSpend === ref.projectSpend)
  );
}

export const sameProject = (a: ProjectRef, b: ProjectRef) =>
  a.projectNo === b.projectNo && a.purchaseTypeNo === b.purchaseTypeNo && a.technicalAccountNo === b.technicalAccountNo && a.projectSpend === b.projectSpend;

export type FollowUpTotals = {
  /** Budget of comparable lines; spend of every unit-sheet row, unbudgeted included. */
  unitBudgets: Figures & { budgetUsd: number };
  projectSpend: Figures;
  /** Committed + accrued of everything ordered in the period. */
  grandTotalUsd: number;
  /** Child lines of the unit budgets, and those over budget in the period. */
  lineCount: number;
  overCount: number;
};

export type FollowUp = {
  units: UnitSheet[];
  projects: ProjectRow[];
  totals: FollowUpTotals;
};

const zero = (): Figures => ({ committedUsd: 0, accruedUsd: 0 });

const add = (into: Figures, s: Figures, share = 1) => {
  into.committedUsd += s.committedUsd * share;
  into.accruedUsd += s.accruedUsd * share;
};

const sum = (ns: number[]) => ns.reduce((a, b) => a + b, 0);

/**
 * A line's budget for the year, or for one quarter: the quarter's own months, as Sertica's
 * follow-up shows it without forecasts (SPEC §4.3 item 4). A January lump is all Q1.
 */
function budgetOfLine(quarter: Quarter | null): (l: BudgetLineInput) => number {
  if (quarter === null) return (l) => sum(l.monthAmounts);
  return (l) => sum(l.monthAmounts.slice((quarter - 1) * 3, quarter * 3));
}

const quarterRule = (b: BudgetInput): QuarterRule =>
  b.lines.every((l) => l.monthAmounts.slice(1).every((m) => m === 0)) ? "january" : "per_quarter";

/** Budget statuses the join uses (SPEC §4.2 item 2). */
export const LIVE_STATUSES = new Set(["Active", "Approved"]);
/** Sertica retires a year's budgets once the year closes: used for a unit with no live budget that year. */
export const RETIRED_STATUS = "Retired";

const keyOf = (...parts: (string | null)[]) => JSON.stringify(parts);

/** One unit's sheet while spend is being joined to it. */
class SheetBuilder {
  /** Child lines by (purchase type, technical account); duplicates within a budget are summed. */
  readonly lines = new Map<
    string,
    {
      purchaseTypeNo: string;
      technicalAccountNo: string;
      budgetUsd: number | null;
      figures: Figures;
      openPo: boolean;
    }
  >();
  readonly purchaseTypes = new Set<string>();
  readonly unbudgeted = new Map<string, Extract<BudgetRow, { kind: "unbudgeted" }>>();

  ambiguous = false;

  constructor(
    readonly unitNo: string | null,
    readonly budget: BudgetInput | null,
    quarter: Quarter | null,
  ) {
    if (!budget) return;
    const lineBudget = budgetOfLine(quarter);
    for (const l of budget.lines) {
      this.purchaseTypes.add(l.purchaseTypeNo);
      if (l.technicalAccountNo === null) continue;
      const k = keyOf(l.purchaseTypeNo, l.technicalAccountNo);
      const line = this.lines.get(k) ?? {
        purchaseTypeNo: l.purchaseTypeNo,
        technicalAccountNo: l.technicalAccountNo,
        budgetUsd: 0,
        figures: zero(),
        openPo: false,
      };
      const comparable =
        budget.monetaryUnit === 1 && (l.currencyNo === null || l.currencyNo === "USD");
      line.budgetUsd =
        comparable && line.budgetUsd !== null ? line.budgetUsd + lineBudget(l) : null;
      this.lines.set(k, line);
    }
  }

  addUnbudgeted(
    reason: UnbudgetedReason,
    purchaseTypeNo: string | null,
    technicalAccountNo: string | null,
    s: Figures,
    share: number,
  ) {
    const k = keyOf(reason, purchaseTypeNo, technicalAccountNo);
    const row = this.unbudgeted.get(k) ?? {
      kind: "unbudgeted",
      reason,
      purchaseTypeNo,
      technicalAccountNo,
      ...zero(),
    };
    add(row, s, share);
    this.unbudgeted.set(k, row);
  }

  /** Puts one share of a PO line on the row it was placed on (see `budgetPlacer`). */
  add(ref: BudgetRowRef, s: Figures & { open?: boolean }, share: number) {
    if (ref.row !== "line")
      return this.addUnbudgeted(ref.row, ref.purchaseTypeNo, ref.technicalAccountNo, s, share);
    const line = this.lines.get(keyOf(ref.purchaseTypeNo, ref.technicalAccountNo))!;
    add(line.figures, s, share);
    if (s.open && share > 0) line.openPo = true;
  }

  /**
   * Rows in sheet order: each purchase type's parent, its child lines and its unbudgeted technical
   * accounts; then the unit-level unbudgeted rows. A parent is the sum of the rows under it.
   */
  build(): UnitSheet {
    // To the cent on both sides, as Over budget judges it: a line over by $0.0088 is over by $0.01, never "−$0.00".
    const remaining = (budgetUsd: number | null, f: Figures) =>
      budgetUsd === null ? null : cents(budgetUsd) - cents(f.committedUsd + f.accruedUsd);
    const unbudgeted = [...this.unbudgeted.values()];
    const rows: BudgetRow[] = [];
    for (const purchaseTypeNo of this.purchaseTypes) {
      const children: BudgetRow[] = [...this.lines.values()]
        .filter((l) => l.purchaseTypeNo === purchaseTypeNo)
        .map(({ technicalAccountNo, budgetUsd, figures, openPo }) => ({
          kind: "line",
          purchaseTypeNo,
          technicalAccountNo,
          budgetUsd,
          ...figures,
          remainingUsd: remaining(budgetUsd, figures),
          over: isOverBudget(budgetUsd, figures),
          openPo,
        }));
      const under = unbudgeted.filter(
        (r) => r.reason === "technical_account_not_budgeted" && r.purchaseTypeNo === purchaseTypeNo,
      );
      const figures = zero();
      for (const r of [...children, ...under]) add(figures, r);
      const childBudgets = children.map((c) => (c.kind === "line" ? c.budgetUsd : null));
      const budgetUsd = childBudgets.includes(null) ? null : sum(childBudgets as number[]);
      rows.push(
        {
          kind: "parent",
          purchaseTypeNo,
          budgetUsd,
          ...figures,
          remainingUsd: remaining(budgetUsd, figures),
          overCount: children.filter((c) => c.kind === "line" && c.over).length,
        },
        ...children,
        ...under,
      );
    }
    rows.push(...unbudgeted.filter((r) => r.reason !== "technical_account_not_budgeted"));
    const lines = rows.filter((r) => r.kind === "line");
    return {
      unitNo: this.unitNo,
      unitName: this.budget?.unitName ?? null,
      budgetNo: this.budget?.budgetNo ?? null,
      status: this.budget?.status ?? null,
      companyName: this.budget?.companyName ?? null,
      quarterRule: this.budget ? quarterRule(this.budget) : null,
      forecast: this.budget?.forecast ?? null,
      ambiguous: this.ambiguous,
      rows,
      lineCount: lines.length,
      overCount: lines.filter((r) => r.over).length,
    };
  }
}

/**
 * One row of a unit's sheet: a child line, or an unbudgeted row and why. Its purchase type and
 * technical account are those the row carries: none for "no live budget" and "uncoded", the
 * purchase type only for "purchase type not budgeted".
 */
export type BudgetRowRef = {
  unitNo: string | null;
  row: "line" | UnbudgetedReason;
  purchaseTypeNo: string | null;
  technicalAccountNo: string | null;
};

export const sameRow = (a: BudgetRowRef, b: BudgetRowRef) =>
  a.unitNo === b.unitNo &&
  a.row === b.row &&
  a.purchaseTypeNo === b.purchaseTypeNo &&
  a.technicalAccountNo === b.technicalAccountNo;

/** The ref of a row on a unit's sheet; null for a parent. */
export function rowRef(unitNo: string | null, r: BudgetRow): BudgetRowRef | null {
  if (r.kind === "parent") return null;
  return {
    unitNo,
    row: r.kind === "line" ? "line" : r.reason,
    purchaseTypeNo: r.purchaseTypeNo,
    technicalAccountNo: r.technicalAccountNo,
  };
}

type PlacementInput = Pick<FollowUpInput, "budgets" | "projectFunded" | "period">;

/**
 * Every unit's budget for the period (§4.2 item 2): its live budget, or, with none, its Retired one
 * (a closed year's budgets are all Retired). The latest updated when two qualify.
 */
function liveSheets(input: PlacementInput): Map<string | null, SheetBuilder> {
  const sheets = new Map<string | null, SheetBuilder>();
  const live = new Map<string, BudgetInput[]>();
  const retired = new Map<string, BudgetInput[]>();
  for (const b of input.budgets) {
    if (b.budgetPeriodNo !== `Year ${input.period.year}`) continue;
    const into = LIVE_STATUSES.has(b.status) ? live : b.status === RETIRED_STATUS ? retired : null;
    into?.set(b.unitNo, [...(into.get(b.unitNo) ?? []), b]);
  }
  for (const unitNo of new Set([...live.keys(), ...retired.keys()])) {
    const candidates = live.get(unitNo) ?? retired.get(unitNo)!;
    const latest = candidates.reduce((a, b) => (b.updateDate > a.updateDate ? b : a));
    const builder = new SheetBuilder(unitNo, latest, input.period.quarter);
    builder.ambiguous = candidates.length > 1;
    sheets.set(unitNo, builder);
  }
  return sheets;
}

/** Where a requisition line's coding lands (§4.2): a row of a unit's sheet, or null for project spend. */
function placeOn(sheets: Map<string | null, SheetBuilder>, projectFunded: Set<string>) {
  return (k: Pick<SpendLink, "unitNo" | "purchaseTypeNo" | "technicalAccountNo">): BudgetRowRef | null => {
    const b = sheets.get(k.unitNo);
    const carried = k.purchaseTypeNo !== null && !!b?.purchaseTypes.has(k.purchaseTypeNo);
    if (k.purchaseTypeNo !== null && projectFunded.has(k.purchaseTypeNo) && !carried) return null;
    const at = (row: BudgetRowRef["row"], pt: string | null, ta: string | null): BudgetRowRef => ({
      unitNo: k.unitNo,
      row,
      purchaseTypeNo: pt,
      technicalAccountNo: ta,
    });
    if (!b?.budget) return at("no_live_budget", null, null);
    if (k.purchaseTypeNo === null || !carried)
      return at("purchase_type_not_budgeted", k.purchaseTypeNo, null);
    if (!b.lines.has(keyOf(k.purchaseTypeNo, k.technicalAccountNo)))
      return at("technical_account_not_budgeted", k.purchaseTypeNo, k.technicalAccountNo);
    return at("line", k.purchaseTypeNo, k.technicalAccountNo);
  };
}

/**
 * The placement the join uses, for coding that is not yet spend: a requisition line not yet
 * ordered goes to the row its spend will go to once it is ordered (SPEC §8.6 line drawer).
 */
export function budgetPlacer(input: PlacementInput) {
  return placeOn(liveSheets(input), new Set(input.projectFunded));
}

/**
 * Called for every share of a PO line the join places: on a unit's row, or on project spend
 * (`to` null, `link` its requisition line).
 */
export type ShareTrace = (
  to: BudgetRowRef | null,
  s: SpendLine,
  link: SpendLink | null,
  share: number,
) => void;

/**
 * The budget follow-up for one period (SPEC §4.2–4.4): every unit's live budget with spend joined
 * to its child lines at read time, and the spend that matches no line as unbudgeted rows.
 */
export function budgetFollowUp(input: FollowUpInput, trace?: ShareTrace): FollowUp {
  const { period } = input;
  const sheets = liveSheets(input);
  const place = placeOn(sheets, new Set(input.projectFunded));
  const projects = new Map<string, ProjectRow>();
  const addProject = (k: SpendLink, s: Figures, share: number, projectSpend: boolean) => {
    if (k.projectNo === null && !projectSpend) return;
    const key = keyOf(k.projectNo, k.purchaseTypeNo, k.technicalAccountNo, String(projectSpend));
    const row = projects.get(key) ?? {
      projectNo: k.projectNo,
      projectName: k.projectName,
      purchaseTypeNo: k.purchaseTypeNo,
      technicalAccountNo: k.technicalAccountNo,
      projectSpend,
      unitNos: [],
      ...zero(),
    };
    add(row, s, share);
    if (!row.unitNos.includes(k.unitNo)) row.unitNos.push(k.unitNo);
    projects.set(key, row);
  };
  const put = (ref: BudgetRowRef, s: SpendLine, link: SpendLink | null, share: number) => {
    if (!sheets.has(ref.unitNo)) sheets.set(ref.unitNo, new SheetBuilder(ref.unitNo, null, period.quarter));
    sheets.get(ref.unitNo)!.add(ref, s, share);
    trace?.(ref, s, link, share);
  };

  for (const s of input.spend) {
    if (s.year !== period.year || (period.quarter !== null && s.quarter !== period.quarter))
      continue;
    if (s.links.length === 0) {
      const row = sheets.get(s.poUnitNo)?.budget ? "uncoded" : "no_live_budget";
      put({ unitNo: s.poUnitNo, row, purchaseTypeNo: null, technicalAccountNo: null }, s, null, 1);
      continue;
    }
    // Several links (allowed, never seen): split by ordered quantity, evenly if that is all zero.
    const totalQty = sum(s.links.map((k) => k.orderedQuantity));
    for (const k of s.links) {
      const share = totalQty > 0 ? k.orderedQuantity / totalQty : 1 / s.links.length;
      const ref = place(k);
      addProject(k, s, share, ref === null);
      if (ref) put(ref, s, k, share);
      else trace?.(null, s, k, share);
    }
  }
  const units = [...sheets.values()].map((b) => b.build());
  const unitBudgets = { budgetUsd: 0, ...zero() };
  for (const r of units.flatMap((u) => u.rows)) {
    if (r.kind === "parent") continue;
    add(unitBudgets, r);
    if (r.kind === "line") unitBudgets.budgetUsd += r.budgetUsd ?? 0;
  }
  const projectTotal = zero();
  for (const r of projects.values()) if (r.projectSpend) add(projectTotal, r);
  const all = (f: Figures) => f.committedUsd + f.accruedUsd;
  return {
    units,
    projects: [...projects.values()],
    totals: {
      unitBudgets,
      projectSpend: projectTotal,
      grandTotalUsd: all(unitBudgets) + all(projectTotal),
      lineCount: sum(units.map((u) => u.lineCount)),
      overCount: sum(units.map((u) => u.overCount)),
    },
  };
}

/** One PO's share of one budget row in the period, for the line drawer (SPEC §8.6). */
export type RowPo = Figures & {
  poNo: string;
  /** The requisitions whose lines on this PO feed the row. */
  requisitionNos: string[];
  /** Ordered or Partly received. */
  open: boolean;
};

/**
 * The line drawer's figures (SPEC §8.6): the row as the sheet shows it, and each PO's share of
 * it, joined exactly as the sheet is. Shares are summed per PO, largest first.
 */
export function budgetRowDetail(input: FollowUpInput, ref: BudgetRowRef) {
  const pos = new Map<string, RowPo>();
  const followUp = budgetFollowUp(input, (to, s, link, share) => {
    if (!to || !sameRow(to, ref)) return;
    const poNo = s.poNo ?? "";
    const po = pos.get(poNo) ?? { poNo, requisitionNos: [], open: s.open, ...zero() };
    add(po, s, share);
    if (link?.requisitionNo && !po.requisitionNos.includes(link.requisitionNo))
      po.requisitionNos.push(link.requisitionNo);
    pos.set(poNo, po);
  });
  const sheet = followUp.units.find((u) => u.unitNo === ref.unitNo) ?? null;
  const row = sheet?.rows.find((r) => {
    const k = rowRef(sheet.unitNo, r);
    return k !== null && sameRow(k, ref);
  });
  const total = (f: Figures) => f.committedUsd + f.accruedUsd;
  return {
    sheet,
    row: row ?? null,
    pos: [...pos.values()].sort((a, b) => total(b) - total(a) || a.poNo.localeCompare(b.poNo)),
  };
}

/** How an unbudgeted row is named on the sheets and in the export (SPEC §4.2 item 6). */
export const UNBUDGETED_LABEL: Record<UnbudgetedReason, string> = {
  no_live_budget: "No budget",
  purchase_type_not_budgeted: "Purchase type not budgeted",
  technical_account_not_budgeted: "Technical account not budgeted",
  uncoded: "Uncoded",
};

/**
 * A vessel's total: every row under it but the parents (which are subtotals of the rest). Its
 * budget is null without a live budget, or when a line is not comparable.
 */
export function sheetTotal(u: UnitSheet): Figures & { budgetUsd: number | null; remainingUsd: number | null } {
  const t = { budgetUsd: u.budgetNo ? 0 : (null as number | null), ...zero() };
  for (const r of u.rows) {
    if (r.kind === "parent") continue;
    add(t, r);
    if (r.otherCostUsd !== undefined) t.otherCostUsd = (t.otherCostUsd ?? 0) + r.otherCostUsd;
    if (r.kind === "line") t.budgetUsd = t.budgetUsd === null || r.budgetUsd === null ? null : t.budgetUsd + r.budgetUsd;
  }
  return { ...t, remainingUsd: t.budgetUsd === null ? null : cents(t.budgetUsd) - cents(t.committedUsd + t.accruedUsd) - cents(t.otherCostUsd ?? 0) };
}

/**
 * Puts the POs under approval (not ordered yet) on a follow-up as each row's other cost, where
 * their coding will be spent once ordered: the row and its parent take the value, and their
 * remaining drops by it. Without it a row has no other cost (undefined), not 0. Meant for a period holding the current quarter, since a PO not yet
 * ordered will be ordered in it; a value whose row is not on the sheet is left out.
 */
export function addOtherCost(f: FollowUp, place: ReturnType<typeof budgetPlacer>, values: Pick<PendingValue, "unitNo" | "purchaseTypeNo" | "technicalAccountNo" | "valueUsd">[]) {
  const take = (r: BudgetRow, usd: number) => {
    r.otherCostUsd = (r.otherCostUsd ?? 0) + usd;
    if (r.kind !== "unbudgeted" && r.remainingUsd !== null) r.remainingUsd = cents(r.remainingUsd - usd);
  };
  // Every row gets one, so a row without POs under approval shows 0 rather than "not shown".
  for (const u of f.units) for (const r of u.rows) r.otherCostUsd ??= 0;
  for (const v of values) {
    const to = place(v);
    const sheet = to && f.units.find((u) => u.unitNo === to.unitNo);
    if (!to || !sheet) continue;
    const i = sheet.rows.findIndex((r) => r.kind !== "parent" && sameRow(rowRef(sheet.unitNo, r)!, to));
    if (i < 0) continue;
    take(sheet.rows[i]!, v.valueUsd);
    const parent = sheet.rows.slice(0, i).findLast((r) => r.kind === "parent");
    // A purchase-type row is the subtotal of its lines and unbudgeted accounts.
    if (parent && (to.row === "line" || to.row === "technical_account_not_budgeted") && parent.purchaseTypeNo === to.purchaseTypeNo) take(parent, v.valueUsd);
  }
}

/**
 * Puts the POs under approval on the Projects list as each row's other cost (SPEC §8.6), where the
 * follow-up would place their spend once ordered: project spend, or on a unit budget. A project
 * with only POs under approval gets its rows. Meant for a period holding the current quarter.
 */
export function addProjectOtherCost(
  f: FollowUp,
  place: ReturnType<typeof budgetPlacer>,
  values: (Pick<PendingValue, "unitNo" | "purchaseTypeNo" | "technicalAccountNo" | "valueUsd"> & { projectNo: string | null; projectName: string | null })[],
) {
  for (const r of f.projects) r.otherCostUsd ??= 0;
  for (const v of values) {
    const projectSpend = place(v) === null;
    if (v.projectNo === null && !projectSpend) continue;
    let row = f.projects.find((r) => onProject(r, v, projectSpend));
    if (!row) {
      row = { projectNo: v.projectNo, projectName: v.projectName, purchaseTypeNo: v.purchaseTypeNo, technicalAccountNo: v.technicalAccountNo, projectSpend, unitNos: [], ...zero(), otherCostUsd: 0 };
      f.projects.push(row);
    }
    row.otherCostUsd = (row.otherCostUsd ?? 0) + v.valueUsd;
    if (!row.unitNos.includes(v.unitNo)) row.unitNos.push(v.unitNo);
  }
}

// ── Over budget flag (SPEC §3.1, §3.2) ──────────────────────────────────────────────────────

/** A requisition line not yet ordered (stages 1–5, on no placed PO) and its coding. */
export type UnorderedLine = {
  requisitionNo: string;
  positionNo: number;
  unitNo: string;
  purchaseTypeNo: string | null;
  technicalAccountNo: string | null;
};

/**
 * A record's value on one coding, not yet ordered: a requisition line's estimate, a PO line's share
 * (PO not ordered yet) or an RFQ's share. `positionNo` is the record's own line; null for an RFQ.
 */
export type PendingValue = {
  no: string;
  positionNo: number | null;
  unitNo: string;
  purchaseTypeNo: string | null;
  technicalAccountNo: string | null;
  valueUsd: number;
};

/** One budget line in the current quarter that a flagged record does not fit, as the drawers list it. */
export type OverBudgetLine = {
  key: string;
  unitNo: string;
  unitName: string | null;
  budgetNo: string | null;
  purchaseTypeNo: string;
  technicalAccountNo: string;
  year: number;
  quarter: Quarter;
  budgetUsd: number;
  /** Committed + accrued in the quarter. */
  usedUsd: number;
  remainingUsd: number;
};

/** A record's own line (or, for an RFQ, its share) on a budget line it does not fit, with its value there. */
export type OverBudgetFeed = { line: string; positionNo: number | null; valueUsd: number };

export type OverBudgetFlags = {
  lines: Map<string, OverBudgetLine>;
  /** Every comparable budget line in the current quarter, flagged or not, by the same key. */
  budgetLines: Map<string, OverBudgetLine>;
  /** A requisition's own committed + accrued on a budget line this quarter, by "requisitionNo|line key". */
  ownSpend: Map<string, number>;
  /** A PO's own committed + accrued on a budget line this quarter, by "poNo|line key". */
  ownPoSpend: Map<string, number>;
  requisitions: Map<string, OverBudgetFeed[]>;
  rfqs: Map<string, OverBudgetFeed[]>;
  pos: Map<string, OverBudgetFeed[]>;
};

export type OverBudgetInput = {
  /** The current year's budgets. */
  budgets: BudgetInput[];
  /** Every PO line ordered in the current quarter: what is already committed and accrued. */
  spend: SpendLine[];
  projectFunded: string[];
  current: { year: number; quarter: Quarter };
  /** Records not ordered yet, by kind: requisitions (stages 1–5), active RFQs, POs not ordered. */
  requisitions: PendingValue[];
  rfqs: PendingValue[];
  pos: PendingValue[];
};

/**
 * The Over budget flag (SPEC §3.1, §3.2), against the last-synced budget lines: a record not yet
 * ordered is flagged when its value on a budget line is more than that line's remaining budget in
 * the current quarter (budget − committed − accrued) by $1.00 or more, both in cents. Its lines are grouped by the
 * budget line they place on; unbudgeted, project-funded and not comparable lines never flag.
 */
export function overBudgetFlags(input: OverBudgetInput): OverBudgetFlags {
  const out: OverBudgetFlags = { lines: new Map(), budgetLines: new Map(), ownSpend: new Map(), ownPoSpend: new Map(), requisitions: new Map(), rfqs: new Map(), pos: new Map() };
  const period = { year: input.current.year, quarter: input.current.quarter };
  const lineKey = (unitNo: string, pt: string | null, ta: string | null) => `${unitNo}|${pt}|${ta}|${period.year}-Q${period.quarter}`;
  const f = budgetFollowUp({ ...input, period }, (to, s, link, share) => {
    if (to?.row !== "line" || to.unitNo === null) return;
    const line = lineKey(to.unitNo, to.purchaseTypeNo, to.technicalAccountNo);
    const usd = (s.committedUsd + s.accruedUsd) * share;
    const add = (m: Map<string, number>, k: string) => m.set(k, (m.get(k) ?? 0) + usd);
    if (link?.requisitionNo !== undefined) add(out.ownSpend, `${link.requisitionNo}|${line}`);
    if (s.poNo !== undefined) add(out.ownPoSpend, `${s.poNo}|${line}`);
  });
  const lines = new Map<string, OverBudgetLine>();
  for (const u of f.units)
    for (const r of u.rows) {
      if (r.kind !== "line" || u.unitNo === null || r.budgetUsd === null) continue;
      lines.set(keyOf(u.unitNo, r.purchaseTypeNo, r.technicalAccountNo), {
        key: lineKey(u.unitNo, r.purchaseTypeNo, r.technicalAccountNo),
        unitNo: u.unitNo,
        unitName: u.unitName,
        budgetNo: u.budgetNo,
        purchaseTypeNo: r.purchaseTypeNo,
        technicalAccountNo: r.technicalAccountNo,
        ...period,
        budgetUsd: r.budgetUsd,
        usedUsd: r.committedUsd + r.accruedUsd,
        remainingUsd: cents(r.budgetUsd) - cents(r.committedUsd + r.accruedUsd),
      });
    }
  for (const l of lines.values()) out.budgetLines.set(l.key, l);
  if (!lines.size) return out;
  const place = budgetPlacer({ ...input, period });
  const judge = (values: PendingValue[], into: Map<string, OverBudgetFeed[]>) => {
    // Each record's value per budget line, with the lines that make it up.
    const on = new Map<string, { no: string; line: OverBudgetLine; feeds: OverBudgetFeed[]; usd: number }>();
    for (const v of values) {
      if (!(v.valueUsd > 0)) continue;
      const to = place(v);
      const line = to?.row === "line" ? lines.get(keyOf(to.unitNo, to.purchaseTypeNo, to.technicalAccountNo)) : undefined;
      if (!line) continue;
      const k = `${v.no}|${line.key}`;
      const x = on.get(k) ?? { no: v.no, line, feeds: [], usd: 0 };
      x.usd += v.valueUsd;
      x.feeds.push({ line: line.key, positionNo: v.positionNo, valueUsd: v.valueUsd });
      on.set(k, x);
    }
    for (const x of on.values()) {
      if (!exceeds(x.usd, x.line.remainingUsd)) continue;
      out.lines.set(x.line.key, x.line);
      into.set(x.no, [...(into.get(x.no) ?? []), ...x.feeds]);
    }
  };
  judge(input.requisitions, out.requisitions);
  judge(input.rfqs, out.rfqs);
  judge(input.pos, out.pos);
  return out;
}

// ── Budgets tab (SPEC §8.6) ─────────────────────────────────────────────────────────────────

export type AttentionKind = "over_now" | "over_earlier" | "close";

/** One budget line in one quarter that the "Needs attention" pane lists. */
export type AttentionRow = Figures & {
  kind: AttentionKind;
  quarter: Quarter;
  unitNo: string | null;
  unitName: string | null;
  budgetNo: string | null;
  purchaseTypeNo: string;
  technicalAccountNo: string;
  budgetUsd: number;
  remainingUsd: number;
};

/** "Close to budget" starts at this share used (SPEC §8.6); such lines are not flagged. */
export const CLOSE_TO_BUDGET = 0.85;

/**
 * Needs attention (SPEC §8.6), judged per quarter as the Over budget flag is: lines over budget in
 * the current quarter; lines over in an earlier quarter that still have open POs ordered in it;
 * and lines at 85 % or more of the current quarter's budget that are not over. Over rows are
 * sorted by the amount over, close rows by the share used.
 */
export function needsAttention(byQuarter: Partial<Record<Quarter, FollowUp>>, current: Quarter): AttentionRow[] {
  const out: Record<AttentionKind, AttentionRow[]> = { over_now: [], over_earlier: [], close: [] };
  for (const [q, f] of Object.entries(byQuarter) as [string, FollowUp][]) {
    const quarter = Number(q) as Quarter;
    if (quarter > current) continue;
    for (const u of f.units)
      for (const r of u.rows) {
        if (r.kind !== "line" || r.budgetUsd === null || r.remainingUsd === null) continue;
        const kind: AttentionKind | null = r.over
          ? quarter === current
            ? "over_now"
            : r.openPo
              ? "over_earlier"
              : null
          : quarter === current && r.budgetUsd > 0 && (r.committedUsd + r.accruedUsd) / r.budgetUsd >= CLOSE_TO_BUDGET
            ? "close"
            : null;
        if (!kind) continue;
        out[kind].push({
          kind,
          quarter,
          unitNo: u.unitNo,
          unitName: u.unitName,
          budgetNo: u.budgetNo,
          purchaseTypeNo: r.purchaseTypeNo,
          technicalAccountNo: r.technicalAccountNo,
          budgetUsd: r.budgetUsd,
          remainingUsd: r.remainingUsd,
          committedUsd: r.committedUsd,
          accruedUsd: r.accruedUsd,
        });
      }
  }
  const used = (r: AttentionRow) => (r.committedUsd + r.accruedUsd) / r.budgetUsd;
  out.over_now.sort((a, b) => a.remainingUsd - b.remainingUsd);
  out.over_earlier.sort((a, b) => a.remainingUsd - b.remainingUsd);
  out.close.sort((a, b) => used(b) - used(a));
  return [...out.over_now, ...out.over_earlier, ...out.close];
}

/** A child line's result for a closed year (SPEC §8.6 year-end result). */
export type YearEndLine = Omit<AttentionRow, "kind" | "quarter">;

/** A closed year's result (SPEC §8.6): in place of Needs attention, which judges the current quarter. */
export type YearEnd = {
  /** Per quarter: child lines over budget in that quarter, of those with a budget. */
  quarters: { quarter: Quarter; overCount: number; lineCount: number }[];
  /** Lines over budget for the year, most over first. */
  over: YearEndLine[];
  /** Lines with budget left at year end, most left first. */
  underspent: YearEndLine[];
};

/** The year's lines over and under budget, and how many were over in each quarter. */
export function yearEnd(year: FollowUp, byQuarter: Record<Quarter, FollowUp>): YearEnd {
  const lines = (f: FollowUp) =>
    f.units.flatMap((u) =>
      u.rows.flatMap((r) =>
        r.kind === "line" && r.budgetUsd !== null && r.remainingUsd !== null
          ? [{ r, row: { unitNo: u.unitNo, unitName: u.unitName, budgetNo: u.budgetNo, purchaseTypeNo: r.purchaseTypeNo, technicalAccountNo: r.technicalAccountNo, budgetUsd: r.budgetUsd, remainingUsd: r.remainingUsd, committedUsd: r.committedUsd, accruedUsd: r.accruedUsd } }]
          : [],
      ),
    );
  const all = lines(year);
  return {
    quarters: ([1, 2, 3, 4] as const).map((quarter) => {
      const ls = lines(byQuarter[quarter]);
      return { quarter, overCount: ls.filter((l) => l.r.over).length, lineCount: ls.length };
    }),
    over: all.filter((l) => l.r.over).map((l) => l.row).sort((a, b) => a.remainingUsd - b.remainingUsd),
    underspent: all.filter((l) => l.row.remainingUsd >= 0.005).map((l) => l.row).sort((a, b) => b.remainingUsd - a.remainingUsd),
  };
}

/** Budget and actual of the same period a year earlier, for a line, a purchase type or a vessel. */
export type PreviousFigures = { budgetUsd: number | null; actualUsd: number };

/**
 * The previous year's figures by row (SPEC §8.6 previous year column): `previousKey` of a child
 * line, a purchase-type parent (technical account null) or a vessel's total (both null).
 */
export function previousFigures(f: FollowUp): Record<string, PreviousFigures> {
  const out: Record<string, PreviousFigures> = {};
  for (const u of f.units) {
    if (!u.budgetNo) continue;
    for (const r of u.rows)
      if (r.kind === "line" || r.kind === "parent")
        out[previousKey(u.unitNo, r.purchaseTypeNo, r.kind === "line" ? r.technicalAccountNo : null)] = { budgetUsd: r.budgetUsd, actualUsd: r.committedUsd + r.accruedUsd };
    const t = sheetTotal(u);
    out[previousKey(u.unitNo, null, null)] = { budgetUsd: t.budgetUsd, actualUsd: t.committedUsd + t.accruedUsd };
  }
  return out;
}

export const previousKey = (unitNo: string | null, purchaseTypeNo: string | null, technicalAccountNo: string | null) =>
  JSON.stringify([unitNo, purchaseTypeNo, technicalAccountNo]);

/** One `budget_line_changes` row with the times of the two syncs it happened between. */
export type BudgetChange = {
  id: number;
  /** edited | added | removed | budget_deleted | log_restarted */
  reason: string;
  budgetNo: string | null;
  unitNo: string | null;
  unitName: string | null;
  purchaseTypeNo: string | null;
  technicalAccountNo: string | null;
  /** Year totals; null when the line appeared or disappeared. */
  oldTotalUsd: number | null;
  newTotalUsd: number | null;
  toRunId: number;
  /** Start of the previous budget sync; null on the first one. */
  fromAt: string | null;
  toAt: string;
};

/** The budget changes found by one budget sync, against the sync before it. */
export type ChangeWindow = {
  toRunId: number;
  fromAt: string | null;
  toAt: string;
  /** The first sync into an empty change log ("log restarted"). */
  restarted: boolean;
  changes: BudgetChange[];
  /** Units whose changes in this window net to zero: "looks like a transfer". A label only. */
  transferUnits: (string | null)[];
};

/** A change's effect on the year total; a line that appeared or disappeared counts from or to 0. */
export const changeAmount = (c: BudgetChange) => (c.newTotalUsd ?? 0) - (c.oldTotalUsd ?? 0);

/**
 * The Budget changes list (SPEC §8.6): grouped by sync window, newest first. When a window's
 * changes on one unit net to zero it is labelled a likely transfer; changes are never paired.
 */
export function changeWindows(changes: BudgetChange[]): ChangeWindow[] {
  const windows = new Map<number, ChangeWindow>();
  for (const c of changes) {
    const w = windows.get(c.toRunId) ?? {
      toRunId: c.toRunId,
      fromAt: c.fromAt,
      toAt: c.toAt,
      restarted: false,
      changes: [],
      transferUnits: [],
    };
    if (c.reason === "log_restarted") w.restarted = true;
    else w.changes.push(c);
    windows.set(c.toRunId, w);
  }
  for (const w of windows.values()) {
    const units = new Map<string | null, BudgetChange[]>();
    for (const c of w.changes) units.set(c.unitNo, [...(units.get(c.unitNo) ?? []), c]);
    for (const [unitNo, cs] of units)
      if (cs.length > 1 && cents(sum(cs.map(changeAmount))) === 0 && cs.some((c) => cents(changeAmount(c)) !== 0))
        w.transferUnits.push(unitNo);
  }
  return [...windows.values()].sort((a, b) => b.toAt.localeCompare(a.toAt));
}

// ── Forecast notes (SPEC §8.6, research/forecasts.md §6) ─────────────────────────────────────

/** What a note on a forecast line does to the line's forecast. */
export type ForecastNoteKind = "shift" | "additional" | "deduction" | "adjustment";

/** A note on a budget line's forecast in Sertica: finance's record of a budget shift or addition. */
export type ForecastNote = {
  /** The forecast month the note changed, 1–12. */
  month: number;
  /** When it was first written, Sertica time. */
  notedAt: string;
  /** Who wrote it: the Sertica login ("user no"); null if unknown. */
  userNo: string | null;
  text: string;
  kind: ForecastNoteKind;
  /** Signed: what the note added to (+) or took from (−) the line; null when the note has no sum. */
  amountUsd: number | null;
};

const NUM = String.raw`-?[\d,]*\.?\d+`;
/** "(start ± amount = end)", start or amount sometimes left out, the amount sometimes negative. */
const SUM_RE = new RegExp(String.raw`\(\s*(${NUM})?\s*([+-])\s*(${NUM})?\s*=\s*(${NUM})\s*\)`, "g");
const num = (s: string) => Number(s.replace(/,/g, ""));
const KINDS: [ForecastNoteKind, RegExp][] = [
  ["shift", /\b(shi?f?t\w*|shfit\w*|swap\w*)\b/i],
  ["additional", /\b(additional|add fund|add budget|open budget|open contract|open docking|tambah\w*)\b/i],
  ["deduction", /\b(deduct\w*|take out|reduc\w*|pengurangan|cabut|potong)\b/i],
];

/**
 * A note's kind, from its words, and its amount, from the sums finance writes at its end, e.g.
 * "shifting budget from spare to store USD 2,000(11261.06-2000=9261.06)" → shift, −2,000. Several
 * sums in one note add up. A sum without its amount, "(2607.14+=62138.14)", is the end less the start.
 */
export function parseForecastNote(text: string): Pick<ForecastNote, "kind" | "amountUsd"> {
  const sums = [...text.matchAll(SUM_RE)].flatMap(([, start, op, amount, end]) =>
    amount !== undefined ? [(op === "-" ? -1 : 1) * num(amount)] : start !== undefined ? [num(end!) - num(start)] : [],
  );
  return {
    kind: KINDS.find(([, re]) => re.test(text))?.[0] ?? "adjustment",
    amountUsd: sums.length ? sums.reduce((a, b) => a + b, 0) : null,
  };
}

/** A budget line's budget in a period, split by where it came from (SPEC §8.6 line drawer). */
export type BudgetMoves = {
  /** The budget less every noted move: what the line started the period with. */
  baseUsd: number;
  additionalUsd: number;
  /** Shifts into the line (+) and out of it (−). */
  shiftInUsd: number;
  shiftOutUsd: number;
  deductionUsd: number;
  adjustmentUsd: number;
};

/** Splits a line's budget into its base and the period's noted moves by kind; they add up to the budget. */
export function budgetMoves(budgetUsd: number, notes: ForecastNote[]): BudgetMoves {
  const of = (keep: (n: ForecastNote) => boolean) => notes.reduce((a, n) => a + (keep(n) ? (n.amountUsd ?? 0) : 0), 0);
  const moves = {
    additionalUsd: of((n) => n.kind === "additional"),
    shiftInUsd: of((n) => n.kind === "shift" && (n.amountUsd ?? 0) > 0),
    shiftOutUsd: of((n) => n.kind === "shift" && (n.amountUsd ?? 0) < 0),
    deductionUsd: of((n) => n.kind === "deduction"),
    adjustmentUsd: of((n) => n.kind === "adjustment"),
  };
  return { baseUsd: budgetUsd - sum(Object.values(moves)), ...moves };
}

/** One row of a budget line's ledger in a period: its base budget, a forecast note, a PO, or a PO under approval reserving budget. */
export type LedgerEntry = { date: string | null; userNo: string | null; amountUsd: number; balanceUsd: number | null } & (
  | { kind: "base" }
  | { kind: "note"; note: ForecastNote }
  | { kind: "po"; poNo: string; open?: boolean }
  | { kind: "reserve"; poNo: string }
);

/** A PO under approval (not ordered yet) on a budget line: its share of the line's other cost (SPEC §8.6). */
export type LedgerReserve = { poNo: string; date: string | null; userNo: string | null; valueUsd: number };

const RANK: Record<LedgerEntry["kind"], number> = { base: 0, note: 1, po: 2, reserve: 3 };

type LedgerPo = { poNo: string; orderDate: string | null; userNo?: string | null; committedUsd: number; accruedUsd: number; open?: boolean };

/**
 * A budget line's movements in a period as a ledger (SPEC §8.6): the base budget at the period's
 * start (the budget less the shifts noted in it, which it already holds), then each note (+/−) and
 * each PO's spend (−) and each PO under approval's reserve (−) by date, a day's notes first, with the
 * running balance, which ends at the remaining budget.
 * Without a budget (unbudgeted rows) there is no base and no balance.
 */
export function budgetLedger(
  budgetUsd: number | null,
  notes: ForecastNote[],
  pos: LedgerPo[],
  periodStart: string,
  reserves: LedgerReserve[] = [],
): LedgerEntry[] {
  const day = (d: string | null) => (d ?? "").slice(0, 10);
  const moves: LedgerEntry[] = [
    ...notes.map((note) => ({ kind: "note" as const, note, date: note.notedAt, userNo: note.userNo, amountUsd: note.amountUsd ?? 0, balanceUsd: null })),
    ...pos.map((p) => ({ kind: "po" as const, poNo: p.poNo, open: p.open, date: p.orderDate, userNo: p.userNo ?? null, amountUsd: -(p.committedUsd + p.accruedUsd), balanceUsd: null })),
    ...reserves.map((r) => ({ kind: "reserve" as const, poNo: r.poNo, date: r.date, userNo: r.userNo, amountUsd: -r.valueUsd, balanceUsd: null })),
    // On one day, budget moves come before the POs that spend them, and POs before reserves.
  ].sort((a, b) => day(a.date).localeCompare(day(b.date)) || RANK[a.kind] - RANK[b.kind] || (a.date ?? "").localeCompare(b.date ?? ""));
  if (budgetUsd === null) return moves;
  const base = budgetUsd - notes.reduce((a, n) => a + (n.amountUsd ?? 0), 0);
  // The balance is the budget so far less the spend so far, each to the cent, so it ends at Remaining.
  let budgetSoFar = base;
  let spentSoFar = 0;
  const balance = (m: LedgerEntry) => {
    if (m.kind === "note") budgetSoFar += m.amountUsd;
    else spentSoFar -= m.amountUsd;
    return cents(budgetSoFar) - cents(spentSoFar);
  };
  // The base precedes every move, so a transfer noted before the period starts (a quarter planned ahead) dates it.
  const first = moves.map((m) => day(m.date)).find(Boolean);
  return [
    { kind: "base", date: first && first < periodStart ? first : periodStart, userNo: null, amountUsd: base, balanceUsd: base },
    ...moves.map((m) => ({ ...m, balanceUsd: balance(m) })),
  ];
}
